import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useCartStore, CartItem } from '@/stores/cartStore'
import { cartSyncService, SavedCart } from '@/services/cartSync'
import { productService } from '@/services/products'

/**
 * Keeps the shopping cart in sync with the customer's profile so it follows them
 * across devices and logins.
 *
 *  - On login (or app boot while already authenticated): fetch the server cart,
 *    merge it with whatever is in the local (guest) cart, and save the result back.
 *  - While authenticated: debounce-save every cart change to the server.
 *  - On logout: clear the local cart (it lives safely server-side now) so a shared
 *    browser never leaks one customer's cart to the next.
 */
export function useCartSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const prevAuth = useRef<boolean | null>(null)
  const synced = useRef(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const toMinimal = (items: CartItem[], referralCode: string | null): SavedCart => ({
    items: items.map((it) => ({
      product_id: it.product.id,
      quantity: it.quantity,
      sim_format: it.sim_format ?? null,
    })),
    referral_code: referralCode,
  })

  // Merge the server cart into the local cart on login / boot.
  useEffect(() => {
    const transitionedToAuthed = prevAuth.current !== true && isAuthenticated
    const transitionedToGuest = prevAuth.current === true && !isAuthenticated
    prevAuth.current = isAuthenticated

    if (transitionedToGuest) {
      // Cart is saved server-side — clear local so the next user starts clean.
      synced.current = false
      useCartStore.getState().clearCart()
      return
    }

    if (!transitionedToAuthed) return

    let cancelled = false
    ;(async () => {
      try {
        const server = await cartSyncService.getCart()
        if (cancelled) return

        // Rehydrate full product details (current prices) for server items.
        const rehydrated: CartItem[] = []
        for (const si of server.items || []) {
          try {
            const product = await productService.getProduct(si.product_id)
            if (product && (product as any).id) {
              rehydrated.push({
                product,
                quantity: si.quantity,
                sim_format: (si.sim_format as any) || undefined,
              })
            }
          } catch { /* product gone / unpublished — skip */ }
        }
        if (cancelled) return

        // Merge: union by product id, take the larger quantity (avoids doubling),
        // keep an explicitly chosen sim_format.
        const local = useCartStore.getState().items
        const byId = new Map<number, CartItem>()
        for (const it of rehydrated) byId.set(it.product.id, { ...it })
        for (const it of local) {
          const existing = byId.get(it.product.id)
          if (existing) {
            existing.quantity = Math.max(existing.quantity, it.quantity)
            existing.sim_format = it.sim_format ?? existing.sim_format
          } else {
            byId.set(it.product.id, { ...it })
          }
        }
        const merged = Array.from(byId.values())
        useCartStore.getState().replaceItems(merged)

        const store = useCartStore.getState()
        if (server.referral_code && !store.referralCode) {
          store.setReferralCode(server.referral_code)
        }

        synced.current = true
        // Persist the merged result so every device converges.
        await cartSyncService.saveCart(toMinimal(merged, useCartStore.getState().referralCode))
      } catch { /* offline / not reachable — local cart still works */ }
    })()

    return () => { cancelled = true }
  }, [isAuthenticated])

  // Debounced save on any cart change while authenticated and after initial sync.
  useEffect(() => {
    const unsub = useCartStore.subscribe((state) => {
      if (!useAuthStore.getState().isAuthenticated || !synced.current) return
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        cartSyncService
          .saveCart(toMinimal(state.items, state.referralCode))
          .catch(() => { /* ignore transient failures */ })
      }, 800)
    })
    return () => {
      unsub()
      if (saveTimer.current) clearTimeout(saveTimer.current)
    }
  }, [])
}
